import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  Clock,
  Image as ImageIcon,
  Sparkles,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { StatusBadge } from "@/components/StatusBadge";
import { useTournament } from "@/hooks/useTournaments";
import { useSocialCalendar, useSocialPosts } from "@/hooks/usePipeline";
import { formatItalianDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";

export default function SocialCalendar() {
  const { slug } = useParams<{ slug: string }>();
  const tournament = useTournament(slug);
  const calendar = useSocialCalendar(slug);
  const posts = useSocialPosts(slug);

  if (!slug) return <div className="text-sm text-slate-500">Slug mancante.</div>;

  const tName = (tournament.data?.input.tournament?.name as string) || slug;
  const status = (tournament.data?.state?.status as string) || "DRAFT";

  const days = calendar.data ?? [];
  const today = dayjs().format("YYYY-MM-DD");
  const totalActions = days.reduce((sum, d) => sum + d.actions.length, 0);
  const totalPublished = days.reduce((sum, d) => sum + d.published.length, 0);

  return (
    <div className="flex flex-col gap-5">
      {/* Back */}
      <div>
        <Link
          to={`/tournaments/${slug}`}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {tName}
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <CalendarDays className="h-6 w-6 text-moving-500" />
            Calendario social
          </h1>
          <p className="text-sm text-slate-500">
            Schedule D-14 → D-0 per {tName} · {totalActions} uscite totali,{" "}
            {totalPublished} già pubblicate
          </p>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Calendar timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Timeline pubblicazioni</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {calendar.isLoading ? (
            <div className="p-6 text-sm text-slate-500">Caricamento…</div>
          ) : days.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">
              Calendario non disponibile.
            </div>
          ) : (
            <ol className="divide-y divide-slate-100">
              {days.map((d) => {
                const isToday = d.date === today;
                const isPast = d.date < today;
                return (
                  <li
                    key={d.date}
                    className={cn(
                      "flex items-start gap-4 px-4 py-3 md:px-5 md:py-4",
                      isToday ? "bg-moving-50" : "",
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg border",
                        isToday
                          ? "border-moving-500 bg-white text-moving-700"
                          : isPast
                            ? "border-slate-200 bg-slate-50 text-slate-400"
                            : "border-slate-200 bg-white text-slate-700",
                      )}
                    >
                      <span className="text-[10px] font-semibold uppercase">
                        {dayjs(d.date).format("MMM")}
                      </span>
                      <span className="text-base font-bold leading-none">
                        {dayjs(d.date).format("DD")}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">
                          {formatItalianDate(d.date, true)}
                        </span>
                        <span
                          className={cn(
                            "rounded px-1.5 py-0.5 text-[10px] font-semibold",
                            d.days_to_tournament === 0
                              ? "bg-red-100 text-red-700"
                              : d.days_to_tournament <= 2
                                ? "bg-amber-100 text-amber-700"
                                : "bg-slate-100 text-slate-600",
                          )}
                        >
                          {d.days_to_tournament === 0
                            ? "GIORNO TORNEO"
                            : `D-${d.days_to_tournament}`}
                        </span>
                        {isToday ? (
                          <span className="inline-flex items-center gap-1 rounded bg-moving-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                            <Clock className="h-3 w-3" />
                            OGGI
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {d.actions.map((a) => {
                          const done = d.published.some((p) =>
                            p.toLowerCase().includes(a.toLowerCase()),
                          );
                          return (
                            <span
                              key={a}
                              className={cn(
                                "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium",
                                done
                                  ? "bg-moving-100 text-moving-700"
                                  : isPast
                                    ? "bg-red-50 text-red-700"
                                    : "bg-slate-100 text-slate-700",
                              )}
                            >
                              {a === "post" ? (
                                <ImageIcon className="h-3 w-3" />
                              ) : (
                                <Sparkles className="h-3 w-3" />
                              )}
                              {a}
                              {done ? <Check className="h-3 w-3" /> : null}
                            </span>
                          );
                        })}
                        {d.actions.length === 0 ? (
                          <span className="text-xs text-slate-400">
                            nessuna uscita schedulata
                          </span>
                        ) : null}
                      </div>
                      {d.published.length > 0 ? (
                        <div className="mt-1 text-[11px] text-slate-500">
                          Pubblicato: {d.published.join(", ")}
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardBody>
      </Card>

      {/* Published posts */}
      {posts.data && posts.data.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Post pubblicati ({posts.data.length})</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <ul className="divide-y divide-slate-100">
              {posts.data.slice(-20).reverse().map((p, i) => (
                <li key={i} className="px-4 py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-court-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-court-700">
                      {p.channel}
                    </span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                      {p.content_type}
                    </span>
                    <span className="text-xs text-slate-500">
                      {p.published_at
                        ? dayjs(p.published_at).format("DD/MM HH:mm")
                        : "scheduled"}
                    </span>
                    {p.success ? (
                      <Check className="h-3.5 w-3.5 text-moving-600" />
                    ) : null}
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-slate-600">
                    {p.caption}
                  </p>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
